import { EventEmitter } from "@ismael1361/utils";

type Modified<D> = { [key in keyof D]: { oldValue: D[key]; newValue: D[key] } };

export class BaseModel<D> extends EventEmitter<{
	change: [modifiedKeys: string[], modified: Modified<D>];
	update: [model: BaseModel<D>];
	clear: [model: BaseModel<D>];
}> {
	private __changes__: Map<
		string | symbol,
		{
			oldValue: any;
			newValue: any;
		}
	> = new Map();
	private __delayEventChange__: NodeJS.Timeout | undefined = undefined;

	constructor() {
		super();

		this.ready(() => {
			this.startTracking();
		});
	}

	/**
	 * Inicia o rastreamento de alterações nas propriedades do objeto.
	 * Converte as propriedades atuais em getters/setters observáveis.
	 * Deve ser chamado ao final do construtor das subclasses.
	 */
	protected startTracking(): void {
		const keys = Object.keys(this) as Array<keyof this>;

		for (const key of keys) {
			// Ignora propriedades internas, funções ou já controladas
			if (typeof key === "string" && key.startsWith("_")) continue;
			if (typeof this[key] === "function") continue;

			const descriptor = Object.getOwnPropertyDescriptor(this, key);
			// Se não for configurável ou não for writable (imutável), pula
			if (descriptor && (!descriptor.configurable || descriptor.writable === false)) continue;

			let value = this[key];

			Object.defineProperty(this, key, {
				get: () => value,
				set: (newValue: any) => {
					if (value !== newValue && this.prepared) {
						clearTimeout(this.__delayEventChange__);
						this.__changes__.set(key as string, { oldValue: value, newValue });
						value = newValue;
						this.__delayEventChange__ = setTimeout(() => {
							this.emit("change", this.modifiedKeys, this.modified);
							this.emit("update", this);
						}, 1000 * 0.5); // Debounce de 500ms
					}
				},
				enumerable: this.propertyIsEnumerable(key),
				configurable: true,
			});
		}
	}

	/**
	 * Verifica se houve alterações na instância.
	 */
	get isDirty(): boolean {
		return this.__changes__.size > 0;
	}

	/**
	 * Retorna a lista de propriedades modificadas.
	 */
	get modifiedKeys(): string[] {
		return Array.from(this.__changes__.keys()) as string[];
	}

	get modified(): Modified<D> {
		const result: any = {};

		for (const [key, change] of this.__changes__) {
			result[key as string] = change;
		}

		return result;
	}

	/**
	 * Confirma as alterações atuais, limpando o estado de "sujo".
	 */
	commit(): void {
		this.__changes__.clear();
	}

	/**
	 * Define uma propriedade como imutável na instância.
	 * O valor não poderá ser alterado após a definição.
	 * Por padrão, a propriedade é enumerável (aparece em loops e JSON), mas isso pode ser controlado com o parâmetro `enumerable`.
	 */
	protected defineImmutable<K extends keyof this>(key: K, enumerable: boolean = true): void {
		Object.defineProperty(this, key, {
			value: this[key as keyof this],
			writable: false,
			enumerable: enumerable,
			configurable: true,
		});
	}

	/**
	 * Converte JSON (string ou objeto) em uma instância do modelo.
	 */
	static parse<T extends BaseModel<any>>(this: new (data?: any) => T, content: string | any): T {
		if (typeof content === "string") {
			return new this(JSON.parse(content));
		}
		return new this(content);
	}

	/**
	 * Converte a instância para um objeto simples (Plain Object).
	 * Útil para serialização e remoção de tipos complexos.
	 */
	toJSON(): D {
		return JSON.parse(this.toString());
	}

	/**
	 * Retorna a representação em string JSON da instância.
	 */
	toString(): string {
		const self: any = {};
		for (const key in this) {
			if (Object.prototype.hasOwnProperty.call(this, key) && !key.startsWith("_") && typeof (this as any)[key] !== "function") {
				self[key] = (this as any)[key];
			}
		}
		return JSON.stringify(self);
	}

	/**
	 * Cria uma cópia profunda da instância.
	 */
	clone(): this {
		const Constructor = this.constructor as new (data?: any) => this;
		return new Constructor(JSON.parse(JSON.stringify(this)));
	}

	clear(data?: Partial<D>): void {
		if (data) {
			this.update(data);
		} else {
			this.prepared = false;
			this.__changes__.clear();
			clearTimeout(this.__delayEventChange__);
			this.prepared = true;
		}

		this.emit("clear", this);
	}

	update(data: Partial<D>): void {
		this.prepared = false;
		this.__changes__.clear();
		clearTimeout(this.__delayEventChange__);

		const keys = Object.keys(data) as Array<keyof D & string>;

		for (const key of keys) {
			const descriptor = Object.getOwnPropertyDescriptor(this, key);
			const value = data[key];

			if (descriptor && !descriptor.writable && descriptor.configurable) {
				Object.defineProperty(this, key, {
					value: value,
					writable: false,
					enumerable: descriptor.enumerable,
					configurable: true,
				});
			} else {
				try {
					// @ts-ignore
					this[key] = value;
				} catch {}
			}
		}

		this.prepared = true;
		this.emit("update", this);
	}
}
